import React, { useEffect, useState } from 'react';
import styled, { DataAttributes } from 'styled-components';
import ClientOnlyPortal from '../../organisms/Modal/ClientOnlyPortal';
import Close from '../../visuals/Icons/Close';

export enum PositionEnum {
  LEFT = 'left',
  TOP = 'top',
  RIGHT = 'right',
  BOTTOM = 'bottom',
}

export type TAnchor = `${PositionEnum}`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  justify-content: center;
  z-index: var(--zindex-modal-backdrop);
  overflow-x: hidden;
  overflow-y: auto;
`;

interface IContainerProps {
  anchor: TAnchor;
}
const Container = styled.div<IContainerProps>`
  background: var(--white);
  border-top-right-radius: ${({ anchor }) =>
    anchor === PositionEnum.BOTTOM || anchor === PositionEnum.LEFT
      ? 'var(--rd-3)'
      : 0};
  border-top-left-radius: ${({ anchor }) =>
    anchor === PositionEnum.BOTTOM || anchor === PositionEnum.RIGHT
      ? 'var(--rd-3)'
      : 0};
  border-bottom-right-radius: ${({ anchor }) =>
    anchor === PositionEnum.TOP || anchor === PositionEnum.LEFT
      ? 'var(--rd-3)'
      : 0};
  border-bottom-left-radius: ${({ anchor }) =>
    anchor === PositionEnum.TOP || anchor === PositionEnum.RIGHT
      ? 'var(--rd-3)'
      : 0};
  position: absolute;
  z-index: var(--zindex-modal);
  ${({ anchor }) => anchor && `${anchor}: 0`};
  min-height: ${({ anchor }) =>
    anchor === PositionEnum.TOP || anchor === PositionEnum.BOTTOM
      ? '90%'
      : '100%'};
  height: ${({ anchor }) =>
    anchor === PositionEnum.TOP || anchor === PositionEnum.BOTTOM
      ? '90%'
      : '100%'};
  width: ${({ anchor }) =>
    anchor === PositionEnum.TOP || anchor === PositionEnum.BOTTOM
      ? '100%'
      : '50%'};
  overflow-y: scroll;
`;

const TitleHead = styled.div`
  display: flex;
  flex-direction: row;
`;

const CloseButton = styled.button.attrs<DataAttributes>(() => ({
  'data-testid': 'Drawer-button-close-drawer',
}))`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  background: none;
  outline: none;
  border: none;
`;

interface IDrawerProps {
  children: React.ReactNode;
  anchor: TAnchor;
  isOpen: boolean;
  externalToggle?: () => void;
  portalSelector?: string;
}

const Drawer: React.FC<IDrawerProps> = ({
  children,
  anchor,
  isOpen,
  externalToggle,
  portalSelector = 'body',
}) => {
  const [isModalOpen, toggleIsModalOpen] = useState(isOpen);
  const [mouseDownTarget, setMouseDownTarget] = useState({});

  useEffect(() => {
    toggleIsModalOpen(isOpen);
  }, [isOpen]);

  const closeModal = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.MouseEvent<HTMLButtonElement>
  ) => {
    if (event.target === mouseDownTarget) {
      toggleIsModalOpen(false);
      externalToggle?.();
    }
  };

  return (
    <ClientOnlyPortal selector={portalSelector}>
      {isModalOpen && (
        <Overlay
          onMouseDown={(event) => setMouseDownTarget(event.target)}
          onClick={closeModal}
        >
          <Container anchor={anchor} onClick={(e) => e.stopPropagation()}>
            <TitleHead>
              <CloseButton type="button" onClick={closeModal}>
                <Close width={11} height={11} />
              </CloseButton>
            </TitleHead>
            {children}
          </Container>
        </Overlay>
      )}
    </ClientOnlyPortal>
  );
};

export default Drawer;
